// src/pages/WorkSchedule.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import api from "../api";
import styles from "./WorkSchedule.module.css";

function eventTitle(s) {
  if (s.is_completed) return `✔${s.completed_by_name || "(처리완료)"} ${s.title}`;
  if (s.is_past) return `(미처리) ${s.title}`;
  const label = s.d_day === 0 ? "D-DAY" : `D-${s.d_day}`;
  return `${label} ${s.title}`;
}

function chipClass(s) {
  if (s.is_completed) return styles.chipDone;
  if (s.is_past) return styles.chipUnprocessed;
  if (s.d_day === 0) return styles.chipToday;
  if (s.d_day <= 3) return styles.chipSoon;
  if (s.d_day === 4) return styles.chipWarn;
  return styles.chipNormal;
}

function formatCompletedAt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const EMPTY_FORM = {
  title: "",
  description: "",
  is_recurring: true,
  day_of_month: "",
  specific_date: "",
  category: "",
  assignee: "",
  is_active: true,
};

export default function WorkSchedule() {
  document.title = "업무일정표";

  const [user, setUser] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [holidays, setHolidays] = useState([]); // [{ date, name }]
  const [missingHolidayYears, setMissingHolidayYears] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [assigneeFilter, setAssigneeFilter] = useState(null); // assignee id or null(전체)
  const [range, setRange] = useState(null); // { start, end } (ISO), FullCalendar datesSet이 채움
  const calendarRef = useRef(null);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // ✅ 클릭한 "그" occurrence를 그대로 보존 — schedules.find(s => s.id === editingId)로
  // 재조회하면 같은 스케줄이 달력 그리드에 2회 이상(월 경계) 나타날 때 엉뚱한 발생일을
  // 완료 처리할 수 있어, 완료/취소는 반드시 이 occurrence 기준으로 동작해야 한다.
  const [selectedOccurrence, setSelectedOccurrence] = useState(null);
  const [completing, setCompleting] = useState(false);

  const fetchUser = async () => {
    try {
      const { data } = await api.get("/users/me/");
      setUser(data);
    } catch (err) {
      console.error("❌ 사용자 정보 불러오기 실패:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/work-schedule/assignable-users/");
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("❌ 담당자 목록 불러오기 실패:", err);
    }
  };

  const fetchSchedules = async (start, end) => {
    setLoading(true);
    try {
      const { data } = await api.get("/work-schedule/schedules/", {
        params: { start, end },
      });
      setSchedules(data.results || []);
      setHolidays(data.holidays || []);
      setMissingHolidayYears(data.missing_holiday_years || []);
    } catch (err) {
      console.error("❌ 일정 불러오기 실패:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
    fetchUsers();
  }, []);

  // ✅ 달력에 보이는 기간(FullCalendar datesSet)이 바뀔 때마다 그 기간을 재조회
  useEffect(() => {
    if (!range) return;
    fetchSchedules(range.start, range.end);
  }, [range]);

  const handleDatesSet = (info) => {
    const start = info.startStr.slice(0, 10);
    const end = info.endStr.slice(0, 10);
    setRange((prev) =>
      prev && prev.start === start && prev.end === end ? prev : { start, end }
    );
  };

  // ✅ holidays가 (비동기로) 늦게 도착해도, 이미 그려진 날짜 셀의
  //    dayCellContent/dayCellClassNames가 FullCalendar 자체 캐시 때문에
  //    자동으로 다시 그려지지 않는 경우가 있어 강제로 재렌더.
  useEffect(() => {
    calendarRef.current?.getApi()?.render();
  }, [holidays]);

  const rank = parseInt(user?.position_rank, 10);
  const isTeamLead = rank === 2 || rank === 3;

  const canEdit = (schedule) => {
    if (!user) return false;
    if (isTeamLead) return true;
    return schedule.created_by === user.id;
  };

  // ── 담당자 범례 (중복 제거) ──────────────────────────
  const assigneeLegend = useMemo(() => {
    const map = new Map();
    schedules.forEach((s) => {
      if (s.assignee && !map.has(s.assignee)) {
        map.set(s.assignee, {
          id: s.assignee,
          name: s.assignee_name,
          color: s.assignee_color,
          initial: s.assignee_initial,
        });
      }
    });
    return Array.from(map.values());
  }, [schedules]);

  const filteredSchedules = useMemo(() => {
    if (!assigneeFilter) return schedules;
    return schedules.filter((s) => s.assignee === assigneeFilter);
  }, [schedules, assigneeFilter]);

  // ── 요약 카드 ────────────────────────────────────
  const todayCount = useMemo(
    () => filteredSchedules.filter((s) => s.d_day === 0).length,
    [filteredSchedules]
  );
  const within4Count = useMemo(
    () => filteredSchedules.filter((s) => s.d_day >= 0 && s.d_day <= 4).length,
    [filteredSchedules]
  );
  const thisMonthCount = useMemo(() => {
    const thisMonth = new Date().getMonth();
    return filteredSchedules.filter(
      (s) => new Date(s.target_date).getMonth() === thisMonth
    ).length;
  }, [filteredSchedules]);

  // ── 공휴일 date→name 매핑 (날짜 칸 배경/숫자/이름 표시용) ──
  const holidayNameByDate = useMemo(() => {
    const map = new Map();
    holidays.forEach((h) => map.set(h.date, h.name));
    return map;
  }, [holidays]);

  const isoOfCellDate = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate()
    ).padStart(2, "0")}`;

  // ── FullCalendar 이벤트 변환 (일정 발생만 — 공휴일은 dayCellContent로 별도 처리) ──
  const events = useMemo(
    () =>
      filteredSchedules.map((s) => ({
        id: s.occurrence_id,
        title: eventTitle(s),
        date: s.target_date,
        extendedProps: s,
      })),
    [filteredSchedules]
  );

  const dayCellClassNames = (info) => {
    return holidayNameByDate.has(isoOfCellDate(info.date)) ? [styles.holidayCell] : [];
  };

  const dayCellContent = (arg) => {
    const holidayName = holidayNameByDate.get(isoOfCellDate(arg.date));
    if (!holidayName) return arg.dayNumberText;
    return (
      <div className={styles.dayCellRow}>
        <span className={styles.holidayName}>{holidayName}</span>
        <span>{arg.dayNumberText}</span>
      </div>
    );
  };

  const renderEventContent = (eventInfo) => {
    const s = eventInfo.event.extendedProps;
    return (
      <div
        className={`${styles.chip} ${chipClass(s)}`}
        onClick={() => openEditModal(s)}
      >
        <span className={styles.chipDot} style={{ background: s.assignee_color }} />
        <span className={styles.chipInitial}>{s.assignee_initial}</span>
        <span>{eventInfo.event.title}</span>
      </div>
    );
  };

  // ── 모달 ────────────────────────────────────────
  const openCreateModal = (dayOfMonth) => {
    setEditingId(null);
    setSelectedOccurrence(null);
    setForm({ ...EMPTY_FORM, day_of_month: dayOfMonth || "" });
    setShowModal(true);
  };

  const openEditModal = (s) => {
    setEditingId(s.id);
    setSelectedOccurrence(s);
    setForm({
      title: s.title,
      description: s.description || "",
      is_recurring: s.is_recurring,
      day_of_month: s.day_of_month || "",
      specific_date: s.specific_date || "",
      category: s.category || "",
      assignee: s.assignee || "",
      is_active: s.is_active,
    });
    setShowModal(true);
  };

  const handleDateClick = (info) => {
    openCreateModal(info.date.getDate());
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setSelectedOccurrence(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      alert("일정명은 필수입니다.");
      return;
    }
    if (form.is_recurring && !form.day_of_month) {
      alert("매월 반복 일정은 며칠인지 입력해야 합니다.");
      return;
    }
    if (!form.is_recurring && !form.specific_date) {
      alert("일회성 일정은 날짜를 선택해야 합니다.");
      return;
    }
    const payload = {
      title: form.title,
      description: form.description,
      is_recurring: form.is_recurring,
      day_of_month: form.is_recurring ? Number(form.day_of_month) : null,
      specific_date: form.is_recurring ? null : form.specific_date,
      category: form.category,
      assignee: form.assignee || null,
      is_active: form.is_active,
    };
    try {
      if (editingId) {
        await api.patch(`/work-schedule/schedules/${editingId}/`, payload);
      } else {
        await api.post("/work-schedule/schedules/", payload);
      }
      closeModal();
      if (range) fetchSchedules(range.start, range.end);
    } catch (err) {
      console.error("❌ 일정 저장 실패:", err);
      alert(err.response?.data?.detail || "저장에 실패했습니다.");
    }
  };

  const handleDelete = async () => {
    if (!editingId) return;
    if (!window.confirm("이 일정을 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/work-schedule/schedules/${editingId}/`);
      closeModal();
      if (range) fetchSchedules(range.start, range.end);
    } catch (err) {
      console.error("❌ 일정 삭제 실패:", err);
      alert("삭제에 실패했습니다.");
    }
  };

  // ✅ 처리완료/완료취소 — 작성자·팀장 제한 없이 로그인 사용자 누구나 가능(요구사항 4).
  // selectedOccurrence의 target_date를 occurrence_date로 그대로 사용 — 같은 스케줄의
  // 다른 발생일에 잘못 적용되지 않도록 editingId(schedule.id)가 아닌 occurrence 자체 기준.
  const toggleComplete = async () => {
    if (!selectedOccurrence || completing) return;
    setCompleting(true);
    try {
      const { data } = selectedOccurrence.is_completed
        ? await api.delete(`/work-schedule/schedules/${selectedOccurrence.id}/complete/`, {
            params: { occurrence_date: selectedOccurrence.target_date },
          })
        : await api.post(`/work-schedule/schedules/${selectedOccurrence.id}/complete/`, {
            occurrence_date: selectedOccurrence.target_date,
          });
      setSelectedOccurrence((prev) => (prev ? { ...prev, ...data } : prev));
      setSchedules((prev) =>
        prev.map((s) =>
          s.occurrence_id === selectedOccurrence.occurrence_id ? { ...s, ...data } : s
        )
      );
    } catch (err) {
      console.error("❌ 완료 처리 실패:", err);
      alert("완료 처리에 실패했습니다.");
    } finally {
      setCompleting(false);
    }
  };

  const editingSchedule = editingId
    ? schedules.find((s) => s.id === editingId)
    : null;
  const canEditCurrent = editingSchedule ? canEdit(editingSchedule) : true; // 신규 등록은 전직원 가능

  return (
    <div className={styles.wrap}>
      <div className={styles.titleRow}>
        <h1 className={styles.title}>업무일정표</h1>
      </div>

      {missingHolidayYears.length > 0 && (
        <div className={styles.banner}>
          ⚠ {missingHolidayYears.join(", ")}년 공휴일이 등록되지 않았습니다. 공휴일 관리에서
          등록해주세요.
        </div>
      )}

      <div className={styles.summaryRow}>
        <div
          className={`${styles.summaryPill} ${
            todayCount > 0 ? styles.summaryPillUrgent : ""
          }`}
        >
          오늘 마감 {todayCount}건
        </div>
        <div className={styles.summaryPill}>D-4 이내 {within4Count}건</div>
        <div className={styles.summaryPill}>이번 달 전체 {thisMonthCount}건</div>

        <button
          type="button"
          className={`${styles.btn} ${styles.btnPrimary} ${styles.registerBtn}`}
          onClick={() => openCreateModal()}
        >
          + 일정 등록
        </button>
      </div>

      {assigneeLegend.length > 0 && (
        <div className={styles.legendRow}>
          <button
            type="button"
            className={`${styles.legendChip} ${
              !assigneeFilter ? styles.legendChipActive : ""
            }`}
            onClick={() => setAssigneeFilter(null)}
          >
            전체
          </button>
          {assigneeLegend.map((a) => (
            <button
              type="button"
              key={a.id}
              className={`${styles.legendChip} ${
                assigneeFilter === a.id ? styles.legendChipActive : ""
              }`}
              onClick={() => setAssigneeFilter(assigneeFilter === a.id ? null : a.id)}
            >
              <span className={styles.legendDot} style={{ background: a.color }} />
              {a.name}
            </button>
          ))}
        </div>
      )}

      <div className={styles.calendarBox} style={{ opacity: loading ? 0.6 : 1 }}>
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          locale="ko"
          height="auto"
          events={events}
          eventContent={renderEventContent}
          dateClick={handleDateClick}
          datesSet={handleDatesSet}
          dayCellClassNames={dayCellClassNames}
          dayCellContent={dayCellContent}
          headerToolbar={{ left: "", center: "prev title next today", right: "" }}
          buttonText={{ today: "오늘" }}
        />
      </div>

      <p className={styles.hintText}>
        날짜를 클릭하면 해당 날짜로 바로 등록할 수 있습니다.
      </p>

      {showModal && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>
              {editingId ? "일정 수정" : "일정 등록"}
            </h3>

            <div className={styles.formRow}>
              <label className={styles.formLabel}>일정명</label>
              <input
                className={styles.formInput}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                disabled={!canEditCurrent}
              />
            </div>

            <div className={styles.formRow}>
              <label className={styles.formLabel}>설명</label>
              <input
                className={styles.formInput}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                disabled={!canEditCurrent}
              />
            </div>

            <div className={styles.formRow}>
              <label className={`${styles.formLabel} ${styles.checkboxLabel}`}>
                <span>매월 반복</span>
                <input
                  type="checkbox"
                  className={styles.checkboxInput}
                  checked={form.is_recurring}
                  onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}
                  disabled={!canEditCurrent}
                />
              </label>
            </div>

            {form.is_recurring ? (
              <div className={styles.formRow}>
                <label className={styles.formLabel}>매월 며칠 (1~31)</label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  className={styles.formInput}
                  value={form.day_of_month}
                  onChange={(e) => setForm({ ...form, day_of_month: e.target.value })}
                  disabled={!canEditCurrent}
                />
              </div>
            ) : (
              <div className={styles.formRow}>
                <label className={styles.formLabel}>날짜</label>
                <input
                  type="date"
                  className={styles.formInput}
                  value={form.specific_date}
                  onChange={(e) => setForm({ ...form, specific_date: e.target.value })}
                  disabled={!canEditCurrent}
                />
              </div>
            )}

            <div className={styles.formRow}>
              <label className={styles.formLabel}>구분</label>
              <input
                className={styles.formInput}
                placeholder="예: 급여 / 보험 / 세무"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                disabled={!canEditCurrent}
              />
            </div>

            <div className={styles.formRow}>
              <label className={styles.formLabel}>담당자</label>
              <select
                className={styles.formInput}
                value={form.assignee}
                onChange={(e) => setForm({ ...form, assignee: e.target.value })}
                disabled={!canEditCurrent}
              >
                <option value="">미지정</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formRow}>
              <label className={`${styles.formLabel} ${styles.checkboxLabel}`}>
                <span>달력에 표시 (체크 해제 시 삭제하지 않고 숨김)</span>
                <input
                  type="checkbox"
                  className={styles.checkboxInput}
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  disabled={!canEditCurrent}
                />
              </label>
            </div>

            {selectedOccurrence && (
              <div className={styles.completionBox}>
                <span className={styles.completionStatus}>
                  {selectedOccurrence.is_completed
                    ? `✔ 처리완료 — ${selectedOccurrence.completed_by_name || "알 수 없음"} · ${formatCompletedAt(selectedOccurrence.completed_at)}`
                    : "이 발생일(회차) 미완료"}
                </span>
                <button
                  type="button"
                  className={`${styles.btn} ${
                    selectedOccurrence.is_completed ? styles.btnSecondary : styles.btnDone
                  }`}
                  onClick={toggleComplete}
                  disabled={completing}
                >
                  {selectedOccurrence.is_completed ? "완료 취소" : "처리완료"}
                </button>
              </div>
            )}

            <div className={styles.modalActions}>
              {editingId && canEditCurrent && (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnDanger}`}
                  onClick={handleDelete}
                >
                  삭제
                </button>
              )}
              <button
                type="button"
                className={`${styles.btn} ${styles.btnPrimary}`}
                onClick={closeModal}
              >
                닫기
              </button>
              {canEditCurrent && (
                <button
                  type="button"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleSubmit}
                >
                  저장
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
